<script setup>
import { ref, watch } from 'vue';
import { useAccount } from 'dashboard/composables/useAccount';
import { useAlert } from 'dashboard/composables';
import Input from 'dashboard/components-next/input/Input.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import SectionLayout from './SectionLayout.vue';

const MIN_EXPIRY_HOURS = 0;
const MAX_EXPIRY_HOURS = 720;
const DEFAULT_EXPIRY_HOURS = 0;

const copy = {
  title: 'Mensagens privadas temporárias',
  description:
    'Escolha se as notas privadas permanecem disponíveis ou são apagadas automaticamente após um prazo.',
  label: 'Validade padrão em horas',
  help: 'Use 0 para nunca expirar. Os atendentes podem escolher outro prazo antes de enviar, de 1 hora a 30 dias.',
  save: 'Salvar validade padrão',
  success: 'Validade padrão das mensagens privadas atualizada.',
  error: 'Não foi possível atualizar a validade padrão.',
};

const { currentAccount, updateAccount } = useAccount();
const expiryHours = ref(DEFAULT_EXPIRY_HOURS);
const isSaving = ref(false);

watch(
  currentAccount,
  account => {
    expiryHours.value = Number(
      account?.settings?.temporary_private_message_default_expiry_hours ??
        DEFAULT_EXPIRY_HOURS
    );
  },
  { immediate: true }
);

const save = async () => {
  const normalizedHours = Math.min(
    MAX_EXPIRY_HOURS,
    Math.max(
      MIN_EXPIRY_HOURS,
      Number(expiryHours.value) || DEFAULT_EXPIRY_HOURS
    )
  );
  expiryHours.value = normalizedHours;
  isSaving.value = true;

  try {
    await updateAccount({
      temporary_private_message_default_expiry_hours: normalizedHours,
    });
    useAlert(copy.success);
  } catch {
    useAlert(copy.error, { variant: 'error' });
  } finally {
    isSaving.value = false;
  }
};
</script>

<template>
  <SectionLayout
    :title="copy.title"
    :description="copy.description"
    with-border
  >
    <div
      class="grid gap-4 rounded-xl border border-n-weak bg-n-solid-2 p-4 sm:grid-cols-[minmax(0,1fr)_auto] sm:items-end"
    >
      <Input
        v-model="expiryHours"
        type="number"
        :min="String(MIN_EXPIRY_HOURS)"
        :max="String(MAX_EXPIRY_HOURS)"
        :label="copy.label"
        :message="copy.help"
      />
      <Button blue :is-loading="isSaving" class="sm:mb-5" @click="save">
        {{ copy.save }}
      </Button>
    </div>
  </SectionLayout>
</template>
